import React, { useMemo } from 'react';
import { Bar, CartesianGrid, ComposedChart, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { DateRange, SeriesSet } from '../../types/dashboard';
import { Panel } from '../ui/Panel';
import { ChartTooltip, LegendItem } from '../ui/ChartTooltip';
import { CHART } from '../../utils/theme';
import { monthLabel, rangeMonths } from '../../utils/calendar';
import { formatAxis, formatValue, isCountMetric, metricMeta, monthValue, type TrendMetric } from '../../utils/metricConfig';
interface TrendPanelProps {
  metric: TrendMetric;
  series: SeriesSet;
  network: SeriesSet;
  range: DateRange;
  scopeUnits: (m: number) => number;
  networkUnits: (m: number) => number;
  unitLabel: 'territory' | 'AOP';
  entityLabel: string;
  showScopeAvg: boolean;
  onMonthClick?: (m: number) => void;
}
interface ChartState {
  activeTooltipIndex?: number;
}
const AXIS_WIDTH = 60;
export function TrendPanel({
  metric,
  series,
  network,
  range,
  scopeUnits,
  networkUnits,
  unitLabel,
  entityLabel,
  showScopeAvg,
  onMonthClick
}: TrendPanelProps) {
  const count = isCountMetric(metric);
  const meta = metricMeta(metric);
  const data = useMemo(() => rangeMonths(range).map(m => {
    const total = monthValue(metric, series.all, m);
    const su = scopeUnits(m);
    const nu = networkUnits(m);
    const net = monthValue(metric, network.all, m);
    return {
      m,
      label: monthLabel(m),
      Digital: monthValue(metric, series.Digital, m),
      Local: monthValue(metric, series.Local, m),
      scope: total,
      network: net,
      scopeAvg: count && su && total !== null ? total / su : null,
      networkAvg: count && nu && net !== null ? net / nu : null
    };
  }),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [metric, series, network, range]);
  const fmt = (v: number) => formatValue(metric, v);
  const axisFmt = (v: number) => formatAxis(metric, v);
  const axis = {
    tick: {
      fontSize: 11.5,
      fill: CHART.axis
    },
    axisLine: false,
    tickLine: false
  } as const;
  const handleClick = (state: ChartState | null) => {
    if (!onMonthClick || !state || state.activeTooltipIndex === undefined) return;
    onMonthClick(range.start + state.activeTooltipIndex);
  };
  const cursor = onMonthClick ? 'pointer' : undefined;
  return <Panel title={`${meta.label} — 13-month trend`} definitionId={meta.definitionId} subtitle={`${range.label} · ${entityLabel} · ${metric === 'members' ? 'new members acquired each month by acquisition date, split by acquisition source' : 'monthly points count members added that month and their same-month registrations'}${onMonthClick ? ' · click a month for detail' : ''}`} data-id="element-17" data-mp="tiqld3xvgfrv">
      {count ? <>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2" data-id="element-18" data-mp="34cv0jpgzbddz">
            <h3 className="text-xs font-semibold text-navy" data-id="element-19" data-mp="39awksdq55ir1">
              {metric === 'members' ? 'New members' : `Total ${meta.label.toLowerCase()}`} · {entityLabel} total · stacked by source
            </h3>
            <div className="flex flex-wrap gap-x-4 gap-y-1" data-id="element-20" data-mp="2wt3l67w0dm3t">
              <LegendItem color={CHART.digital} label="Digital" data-id="element-21" data-mp="3aph3fj8d9rjc" />
              <LegendItem color={CHART.local} label="Local" data-id="element-22" data-mp="35rxftsps42eq" />
            </div>
          </div>
          <div className="h-[270px]" data-id="element-23" data-mp="2i1tmce6b1j1g">
            <ResponsiveContainer width="100%" height="100%" data-id="element-24" data-mp="2i2wc2irw5ffs">
              <ComposedChart data={data} syncId="trend" margin={{
            top: 6,
            right: 8,
            left: 0,
            bottom: 0
          }} onClick={handleClick} barCategoryGap="22%" data-id="element-25" data-mp="2e0rmpw8h74yy">
                <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-26" data-mp="2rth75w8cw1ty" />
                <XAxis dataKey="label" {...axis} axisLine={{
              stroke: CHART.grid
            }} data-id="element-27" data-mp="2n0xy9ckm5uvh" />
                <YAxis tickFormatter={axisFmt} width={AXIS_WIDTH} {...axis} label={{
              value: 'Total',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: {
                fontSize: 11,
                fill: CHART.muted
              }
            }} data-id="element-28" data-mp="1zb3hwl135o6h" />
                <Tooltip cursor={{
              fill: 'rgba(20,48,92,0.05)'
            }} content={<ChartTooltip formatter={v => fmt(v)} footer={onMonthClick ? 'Click for month detail' : undefined} data-id="element-30" data-mp="2cyu82qful8lm" />} data-id="element-29" data-mp="2cy2l240bgj6y" />
                <Bar dataKey="Digital" name="Digital" stackId="src" fill={CHART.digital} maxBarSize={46} isAnimationActive={false} cursor={cursor} data-id="element-31" data-mp="2hvkkn3sg1m2a" />
                <Bar dataKey="Local" name="Local" stackId="src" fill={CHART.local} radius={[3, 3, 0, 0]} maxBarSize={46} isAnimationActive={false} cursor={cursor} data-id="element-32" data-mp="2qop8v14jgoo9" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 border-t border-line pt-3" data-id="element-33" data-mp="30ltu8yqk8g40">
            <div className="mb-1 flex flex-wrap items-center justify-between gap-2" data-id="element-34" data-mp="35lyff22exz0k">
              <h3 className="text-xs font-semibold text-navy" data-id="element-35" data-mp="16ankwjsvs447">
                Avg {metric === 'members' ? 'new members' : meta.label.toLowerCase()} per active {unitLabel} · monthly total ÷ active {unitLabel === 'AOP' ? 'AOPs' : 'territories'} that month · own scale
              </h3>
              <div className="flex flex-wrap gap-x-4 gap-y-1" data-id="element-36" data-mp="3js02b05qx06n">
                {showScopeAvg && <LegendItem color={CHART.navy} label={`${entityLabel} avg`} line data-id="element-37" data-mp="3fqy0qcocwc1i" />}
                <LegendItem color={CHART.accent} label={`Network avg per active ${unitLabel}`} line dashed data-id="element-38" data-mp="3fotvnreq16zn" />
              </div>
            </div>
            <div className="h-[120px]" data-id="element-39" data-mp="bczx7zlbj2e2">
              <ResponsiveContainer width="100%" height="100%" data-id="element-40" data-mp="bbx3ljwm2lug">
                <LineChart data={data} syncId="trend" margin={{
              top: 6,
              right: 8,
              left: 0,
              bottom: 0
            }} onClick={handleClick} data-id="element-41" data-mp="7c6852w53hjl">
                  <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-42" data-mp="2a72tnguaak0" />
                  <XAxis dataKey="label" {...axis} axisLine={{
                stroke: CHART.grid
              }} tick={false} height={4} data-id="element-43" data-mp="3th9v6i8eymvt" />
                  <YAxis tickFormatter={axisFmt} width={AXIS_WIDTH} {...axis} domain={['auto', 'auto']} tickCount={3} label={{
                value: 'Avg',
                angle: -90,
                position: 'insideLeft',
                offset: 10,
                style: {
                  fontSize: 11,
                  fill: CHART.muted
                }
              }} data-id="element-44" data-mp="2n3257baiwq6b" />
                  <Tooltip content={<ChartTooltip formatter={v => fmt(v)} data-id="element-46" data-mp="2ailrxu2zwcfv" />} data-id="element-45" data-mp="2akgaj1qsnsgs" />
                  {showScopeAvg && <Line type="monotone" dataKey="scopeAvg" name={`${entityLabel} avg`} stroke={CHART.navy} strokeWidth={2} dot={{
                r: 2.5,
                fill: CHART.navy
              }} isAnimationActive={false} data-id="element-47" data-mp="2fhymz240sptj" />}
                  <Line type="monotone" dataKey="networkAvg" name={`Network avg / ${unitLabel}`} stroke={CHART.accent} strokeWidth={2} strokeDasharray="5 4" dot={false} isAnimationActive={false} data-id="element-48" data-mp="25kai3jcpa1xn" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </> : <>
          <div className="mb-2 flex flex-wrap items-center gap-x-4 gap-y-1" data-id="element-49" data-mp="1hw0ymo2ylq9o">
            <LegendItem color={CHART.navy} label={entityLabel} line data-id="element-50" data-mp="1mulxaj2kc1w0" />
            <LegendItem color={CHART.accent} label="Network" line dashed data-id="element-51" data-mp="1mvx1ul3u1do6" />
            <LegendItem color={CHART.digital} label="Digital" line data-id="element-52" data-mp="138j2wf7dk5ze" />
            <LegendItem color={CHART.local} label="Local" line data-id="element-53" data-mp="ya96y2dtt1cq" />
          </div>
          <div className="h-[320px]" data-id="element-54" data-mp="1d6zw7itscwom">
            <ResponsiveContainer width="100%" height="100%" data-id="element-55" data-mp="1d81kioztbfoa">
              <LineChart data={data} margin={{
            top: 8,
            right: 12,
            left: 0,
            bottom: 0
          }} onClick={handleClick} data-id="element-56" data-mp="186ub6li0u8fi">
                <CartesianGrid vertical={false} stroke={CHART.grid} data-id="element-57" data-mp="1m6daqpwrobg7" />
                <XAxis dataKey="label" {...axis} axisLine={{
              stroke: CHART.grid
            }} data-id="element-58" data-mp="1h8boxx7yhy11" />
                <YAxis tickFormatter={axisFmt} width={AXIS_WIDTH} {...axis} domain={metric === 'conversion' ? [0, 'auto'] : ['auto', 'auto']} data-id="element-59" data-mp="1h67l1fa24z0r" />
                <Tooltip content={<ChartTooltip formatter={v => fmt(v)} footer={onMonthClick ? 'Click for month detail' : undefined} data-id="element-61" data-mp="1qz6epbkevfr8" />} data-id="element-60" data-mp="1qywktcw8wog9" />
                <Line type="monotone" dataKey="Digital" name="Digital" stroke={CHART.digital} strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} data-id="element-62" data-mp="24vju8f5dx732" />
                <Line type="monotone" dataKey="Local" name="Local" stroke={CHART.local} strokeWidth={1.5} dot={false} connectNulls isAnimationActive={false} data-id="element-63" data-mp="379xq9hg7gb2j" />
                <Line type="monotone" dataKey="network" name="Network" stroke={CHART.accent} strokeWidth={2} strokeDasharray="5 4" dot={false} connectNulls isAnimationActive={false} data-id="element-64" data-mp="3h96myi2gg3sl" />
                <Line type="monotone" dataKey="scope" name={entityLabel} stroke={CHART.navy} strokeWidth={2.5} dot={{
              r: 3,
              fill: CHART.navy
            }} connectNulls isAnimationActive={false} cursor={cursor} data-id="element-65" data-mp="3l8fh3o1eq3oo" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>}
    </Panel>;
}